// ✅ src/pages/VehicleV2/BillingReport.jsx
// Production listing template
// GET /api/billingReport/stores
// GET /api/billingReport/vehicleMainTypes
// GET /api/billingReport/branches
// GET /api/billingReport/list

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import {
  FaSearch,
  FaEye,
  FaPrint,
  FaArrowLeft,
  FaFileExport,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://nissi-india.com/api/billingReport";

const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const fmtDateDDMMYYYY = (v) => {
  if (!v) return "";
  const s = toStr(v);
  if (s.includes("/")) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && toStr(v) !== "") return v;
  }
  return fallback;
};

const exportToCsv = (rows, filename = "billing-report.csv") => {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`)
        .join(",")
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default function BillingReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  const branchType = sessionBranchType || "BRANCH";
  const branchId = sessionBranchId || "";
  const loginUserId = userId || "";

  const [showFilters, setShowFilters] = useState(true);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const isBusy = loadingMeta || loadingList;
  const [stores, setStores] = useState([]);
  const [vehicleMainTypes, setVehicleMainTypes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    vehicleType: "Select",
    branch: branchType === "HO" ? "Select" : String(branchId || ""),
    store: "Select",
    vehicleMainType: "Select",
  });
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fieldClass = `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const loadDropdowns = async () => {
    try {
      setLoadingMeta(true);
      const reqs = [
        axios.get(`${API_BASE}/stores`, { params: { branchId: branchId || "" }, headers: apiHeaders }),
        axios.get(`${API_BASE}/vehicleMainTypes`, { headers: apiHeaders }),
      ];
      if (branchType === "HO") reqs.push(axios.get(`${API_BASE}/branches`, { headers: apiHeaders }));
      const [storesRes, mainTypesRes, branchesRes] = await Promise.all(reqs);
      setStores(storesRes.data?.rows || []);
      setVehicleMainTypes(mainTypesRes.data?.rows || []);
      setBranches(branchesRes?.data?.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Dropdown load failed");
    } finally {
      setLoadingMeta(false);
    }
  };

  const loadList = async (f = filters, silent = false) => {
    try {
      setLoadingList(true);
      const { data } = await axios.get(`${API_BASE}/list`, {
        params: {
          branchType,
          branchId,
          loginUserId,
          fromDate: f.fromDate || "",
          toDate: f.toDate || "",
          vehicleType: isSelect(f.vehicleType) ? "" : f.vehicleType,
          branch: isSelect(f.branch) ? "" : f.branch,
          store: isSelect(f.store) ? "" : f.store,
          vehicleMainType: isSelect(f.vehicleMainType) ? "" : f.vehicleMainType,
        },
        headers: apiHeaders,
      });
      setRows(data?.rows || []);
      setCurrentPage(1);
      if (!silent) toast.success(`Found ${(data?.rows || []).length} records`);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "List load failed");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (!branchId || !branchType || !loginUserId) {
      toast.error("Session data missing in localStorage");
      return;
    }
    loadDropdowns();
  }, []);

  const handleChange = (e) => setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));
  const handleSearch = () => loadList(filters);
  const handleViewAll = () => {
    const reset = {
      fromDate: "",
      toDate: "",
      vehicleType: "Select",
      branch: branchType === "HO" ? "Select" : String(branchId || ""),
      store: "Select",
      vehicleMainType: "Select",
    };
    setFilters(reset);
    loadList(reset, true);
    toast.info("Showing all records");
  };
  const handleClearFilters = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      vehicleType: "Select",
      store: "Select",
      vehicleMainType: "Select",
      branch: branchType === "HO" ? "Select" : String(branchId || ""),
    });
    setRows([]);
    setCurrentPage(1);
    toast.info("Filters cleared");
  };

  const handlePrint = (row) => {
    const billId = pick(row, ["fld_billid", "BillID", "billId", "fld_billId"], 0);
    if (!billId) return toast.error("Bill ID not found in row");
    navigate("/vehicle/VehicleBillGenerationReport_Print", { state: { billId } });
  };

  const getBillNo = (r) => pick(r, ["BillNo", "fld_billno", "billNo"], "-");
  const getBranchName = (r) => pick(r, ["BranchName", "fld_branchName", "fld_branchname", "branch"], "-");
  const getVehicleCode = (r) => pick(r, ["fld_vehicle_code", "VehicleCode"], "");
  const getVehicleName = (r) => pick(r, ["fld_vehicle_name", "VehicleName"], "");
  const getVehicleReg = (r) => pick(r, ["fld_vehicle_regno", "VehicleRegNo"], "");
  const getVehicleNumber = (r) => pick(r, ["VehicleNumber", "vehicleNumber"], "") || getVehicleReg(r) || getVehicleCode(r) || "-";
  const getDate = (r) => fmtDateDDMMYYYY(pick(r, ["Date", "fld_Crdate", "fld_date", "date"], ""));
  const getAmount = (r) => pick(r, ["AmountToPay", "fld_balance_payable", "amount"], "0.00");

  const totalPages = Math.max(1, Math.ceil(rows.length / itemsPerPage));
  const currentItems = rows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <VehicleLayoutWrapper>
      <div className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl md:text-3xl">BILL GENERATION REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
          isDark ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white" : "bg-white text-black"
        }`}>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>Billing Report</h2>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button onClick={() => { if (!rows.length) return toast.info("No data to export"); exportToCsv(rows); toast.success("Exported to CSV"); }} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}><FaFileExport /> Export</button>
              <button onClick={loadDropdowns} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSyncAlt /> Refresh</button>
              <button onClick={() => navigate(-1)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}><FaArrowLeft /> Back</button>
              <button onClick={() => setShowFilters((s) => !s)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>Search Criteria</h3>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={handleClearFilters} type="button" className={`px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"}`}>Clear</button>
                  <button onClick={handleViewAll} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaEye /> View All</button>
                  <button onClick={handleSearch} disabled={isBusy} type="button" className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSearch /> {loadingList ? "Searching..." : "Search"}</button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className={labelClass}>From Date</label>
                  <input type="date" name="fromDate" value={filters.fromDate} onChange={handleChange} className={fieldClass} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>To Date</label>
                  <input type="date" name="toDate" value={filters.toDate} onChange={handleChange} className={fieldClass} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Type (H/O)</label>
                  <select name="vehicleType" value={filters.vehicleType} onChange={handleChange} className={fieldClass}>
                    <option value="Select">Select</option>
                    <option value="H">Hired</option>
                    <option value="O">Own</option>
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Branch</label>
                  <select name="branch" value={filters.branch} onChange={handleChange} className={fieldClass} disabled={branchType !== "HO"}>
                    <option value="Select">Select</option>
                    {branchType === "HO" ? (
                      branches.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)
                    ) : (
                      <option value={String(branchId || "")}>Branch {branchId}</option>
                    )}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Store</label>
                  <select name="store" value={filters.store} onChange={handleChange} className={fieldClass}>
                    <option value="Select">Select</option>
                    {stores.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select name="vehicleMainType" value={filters.vehicleMainType} onChange={handleChange} className={fieldClass}>
                    <option value="Select">Select</option>
                    {vehicleMainTypes.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>Total Records: {rows.length}</div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{loadingMeta ? "Loading dropdowns..." : loadingList ? "Searching..." : "Ready"}</div>
          </div>

          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {loadingMeta || loadingList ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}><div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div></div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Billing Records Found</div>
                <p className={`mt-2 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>Adjust filters or click View All.</p>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">S.No</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Bill No</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Vehicle Number</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Branch</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Date</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Amount</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Print</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {currentItems.map((r, idx) => (
                    <tr key={pick(r, ["fld_billid", "BillID", "billId"], `${idx}`)} className={`${idx % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-white") : (isDark ? "bg-gray-750" : "bg-gray-50")} ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{getBillNo(r)}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{getVehicleNumber(r)}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{getBranchName(r)}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{getDate(r) || "-"}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{getAmount(r)}</td>
                      <td className="px-3 py-3">
                        <button onClick={() => handlePrint(r)} type="button" className={`p-2 rounded ${isDark ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-100 text-gray-800 hover:bg-gray-200"}`} title="Print">
                          <FaPrint className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {rows.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, rows.length)} of {rows.length} entries
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""}`}>Prev</button>
                <div className={`px-4 py-2 border rounded-lg ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}>Page {currentPage} / {totalPages}</div>
                <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""}`}>Next</button>
              </div>
            </div>
          )}

          <ToastContainer position="top-right" autoClose={2500} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}